// CommentListExample.tsx
import React, { useEffect, useRef, useState } from 'react';
import CommentList from './commentList.tsx';
import CommentForm from '../commentForm.tsx';
import io, { Socket } from 'socket.io-client';

interface CommentItem {
  id: number;
  username: string;
  date: string;
  time: string;
  text: string;
  parentId: number | null;
  postId: number;
  seriesId: number;
  replies: CommentItem[];
}

interface CommentListExampleProps {
  postId: number;
  seriesId: number;
}

const API_URL = 'https://api-rifqifarhansyah-id.onrender.com';

const CommentListExample: React.FC<CommentListExampleProps> = ({
  postId,
  seriesId,
}) => {
  const [comments, setComments] = useState<CommentItem[] | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const handleReplyClick = (commentId: number) => {
    setSelectedCommentId(commentId);
  };

  // =========================
  // FETCH COMMENTS
  // =========================
  const fetchComments = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/comments?seriesId=${seriesId}&postId=${postId}`
      );

      const data = await res.json();

      const mapped: CommentItem[] = data.map((comment: any) => ({
        id: comment.CommentID,
        username: comment.UserName,
        date: comment.DatePost?.split('T')[0],
        time: comment.TimePost?.slice(0, 5),
        text: comment.Content,
        parentId: comment.ParentID,
        postId: comment.PostID,
        seriesId: comment.SeriesID,
        replies: [],
      }));

      setComments(mapped);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    setComments(null); // reset ke loading saat post/series berubah
    fetchComments();
  }, [postId, seriesId]);

  // =========================
  // SOCKET.IO
  // =========================
  useEffect(() => {
    socketRef.current = io(API_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    socketRef.current.on('message', (msg: string) => {
      try {
        const data = JSON.parse(msg);

        if (
          data.action === 'newComment' &&
          data.postId === postId &&
          data.seriesId === seriesId
        ) {
          fetchComments();
        }
      } catch (err) {
        console.error('Socket message parse error:', err);
      }
    });

    return () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [postId, seriesId]);

  const isLoading = comments === null;
  const commentCount = comments?.length ?? 0;
  const commentLabel = commentCount <= 1 ? 'Comment' : 'Comments';

  return (
    <div className="min-w-screen">
      {/* TITLE */}
      {!isLoading && (
        <h1 className="text-textGrey mt-8 font-condensed font-extrabold md:text-4xl text-2xl">
          {commentCount} {commentLabel}
        </h1>
      )}

      {/* LOADING TEXT */}
      {isLoading && (
        <h1 className="text-textGrey mt-8 font-condensed font-extrabold md:text-4xl text-2xl">
          Loading
          <span className="inline-block animate-pulse">...</span>
        </h1>
      )}

      {/* DESCRIPTION */}
      <p className="text-textGrey font-condensed md:text-lg text-sm px-4 mt-1">
        {/* {isLoading && (
          <span className="block text-textGrey mb-1">
            Loading
            <span className="inline-block animate-pulse">...</span>
          </span>
        )} */}
        Leave your thoughts, feedback, or anything else you'd like to share.
      </p>

      {/* LOADING BAR */}
      <div className="relative w-5/6 mx-auto mt-3 mb-6 h-[2px] bg-gray-200 overflow-hidden">
        <div
          className={`absolute left-0 top-0 h-full bg-textRed transition-all duration-700 ${
            isLoading ? 'w-1/3 animate-pulse' : 'w-full'
          }`}
        />
      </div>

      {/* COMMENTS */}
      {!isLoading && comments && (
        <>
          <CommentList
            comments={comments}
            onReplyClick={handleReplyClick}
            selectedCommentId={selectedCommentId}
            postId={postId}
            seriesId={seriesId}
          />

          {!selectedCommentId && (
            <CommentForm postId={postId} seriesId={seriesId} />
          )}
        </>
      )}
    </div>
  );
};

export default CommentListExample;
